import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getStatusConfig = (status) => {
  const map = {
    pending: { label: 'Order Received', step: 1, color: '#C97A2B' },
    confirmed: { label: 'Confirmed', step: 2, color: '#C97A2B' },
    preparing: { label: 'Preparing', step: 3, color: '#C97A2B' },
    ready: { label: 'Ready', step: 4, color: '#6B705C' },
    out_for_delivery: { label: 'On the Way', step: 5, color: '#B65A3A' },
    delivered: { label: 'Delivered', step: 6, color: '#2F2F2F' },
    cancelled: { label: 'Cancelled', step: 0, color: '#B65A3A' },
  };
  return map[status] || map.pending;
};

const StatusTimeline = ({ currentStatus }) => {
  const steps = [
    { key: 'pending', label: 'Received' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'preparing', label: 'Preparing' },
    { key: 'ready', label: 'Ready' },
    { key: 'out_for_delivery', label: 'On the Way' },
    { key: 'delivered', label: 'Delivered' },
  ];
  const currentStep = getStatusConfig(currentStatus).step;

  if (currentStatus === 'cancelled') {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </div>
        <p className="text-[#B65A3A] font-medium tracking-wide uppercase text-sm">Order Cancelled</p>
      </div>
    );
  }

  return (
    <div className="py-4">
      {/* Progress bar */}
      <div className="relative flex items-center justify-between mb-6">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-[#D9D9D6]" />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-[#C97A2B] transition-all duration-700"
          style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
        />
        {steps.map((step, i) => {
          const done = currentStep > i + 1;
          const active = currentStep === i + 1;
          return (
            <div key={step.key} className="relative z-10 flex flex-col items-center">
              <motion.div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                  done ? 'bg-[#C97A2B] border-[#C97A2B] text-white' :
                  active ? 'bg-white border-[#C97A2B] text-[#C97A2B]' :
                  'bg-white border-[#D9D9D6] text-[#D9D9D6]'
                }`}
                animate={active ? { scale: [1, 1.15, 1] } : {}}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                {done ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                ) : i + 1}
              </motion.div>
              <span className={`mt-2 text-[10px] tracking-wide uppercase whitespace-nowrap ${active ? 'text-[#C97A2B] font-medium' : done ? 'text-[#2F2F2F]' : 'text-[#D9D9D6]'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OrderCard = ({ order, onViewDetails }) => {
  const statusCfg = getStatusConfig(order.status);
  const isActive = !['delivered', 'cancelled'].includes(order.status);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-lg border ${isActive ? 'border-[#C97A2B]/30 shadow-md' : 'border-[#D9D9D6]'} overflow-hidden`}
      data-testid={`order-card-${order.id?.slice(0, 8)}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#D9D9D6]/50">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B705C]">Order</p>
          <p className="text-[#2F2F2F] font-medium" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            #{order.id?.slice(0, 8).toUpperCase()}
          </p>
        </div>
        <div className="text-right">
          <span
            className="inline-block px-3 py-1 rounded-full text-[10px] tracking-wide uppercase font-medium"
            style={{ backgroundColor: `${statusCfg.color}15`, color: statusCfg.color }}
          >
            {statusCfg.label}
          </span>
        </div>
      </div>

      {/* Timeline for active orders */}
      {isActive && (
        <div className="px-5">
          <StatusTimeline currentStatus={order.status} />
        </div>
      )}

      {/* Items */}
      <div className="px-5 py-3 space-y-2">
        {order.items?.map((item, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-[#2F2F2F]">{item.name} <span className="text-[#6B705C]">x{item.quantity}</span></span>
            <span className="text-[#2F2F2F] font-medium">{item.price}</span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-4 bg-[#F5F3EF]/60 border-t border-[#D9D9D6]/50">
        <div>
          {order.estimatedDeliveryTime && isActive && (
            <p className="text-xs text-[#C97A2B] font-medium">
              ETA: {new Date(order.estimatedDeliveryTime).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          <p className="text-xs text-[#6B705C]">{order.paymentMethod || 'Cash on Delivery'}</p>
        </div>
        <p className="text-xl font-bold text-[#2F2F2F]" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
          {order.totalPrice}
        </p>
      </div>
    </motion.div>
  );
};

const OrderLookup = () => {
  const [phone, setPhone] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setError('Please enter your phone number'); return; }
    setLoading(true);
    setError('');
    setSearched(false);
    try {
      const res = await axios.get(`${API}/api/orders/lookup`, { params: { phone } });
      setOrders(res.data.orders || []);
      setSearched(true);
      if (!res.data.orders?.length) setError('No orders found for this phone number.');
    } catch {
      setError('Failed to look up orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="order-lookup-page">
      <div className="max-w-2xl mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="text-[#6B705C] text-xs tracking-[0.3em] uppercase mb-3">Real-time Updates</p>
          <h1
            className="text-3xl md:text-4xl text-[#2F2F2F] mb-3"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Track Your <span className="text-[#C97A2B] italic">Order</span>
          </h1>
          <p className="text-[#6B705C] text-sm font-light max-w-md mx-auto">
            Enter the phone number you used when placing your order to see its current status.
          </p>
        </motion.div>

        {/* Search */}
        <motion.form
          onSubmit={handleSearch}
          className="bg-white rounded-lg border border-[#D9D9D6] p-5 mb-8 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          data-testid="order-lookup-form"
        >
          <label className="block text-[10px] tracking-[0.2em] uppercase text-[#6B705C] mb-2">Phone Number</label>
          <div className="flex gap-3">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+358 40 123 4567"
              className="flex-1 px-4 py-3 border border-[#D9D9D6] rounded text-[#2F2F2F] placeholder-[#D9D9D6] focus:outline-none focus:border-[#C97A2B] transition-colors text-sm"
              data-testid="phone-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-[#2F2F2F] text-white text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#C97A2B] transition-colors disabled:opacity-50"
              data-testid="lookup-submit-btn"
            >
              {loading ? 'Searching...' : 'Track'}
            </button>
          </div>
          {error && (
            <p className="mt-3 text-sm text-[#B65A3A]" data-testid="lookup-error">{error}</p>
          )}
        </motion.form>

        {/* Results */}
        <AnimatePresence>
          {searched && orders.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-5"
            >
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B705C]">
                {orders.length} order{orders.length > 1 ? 's' : ''} found
              </p>
              {orders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty state */}
        {searched && orders.length === 0 && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D9D9D6]/30 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-[#6B705C]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>
            <p className="text-[#2F2F2F] font-medium mb-1">No orders found</p>
            <p className="text-[#6B705C] text-sm">Try a different phone number or place a new order.</p>
          </motion.div>
        )}

        {/* Help */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-8 h-[1px] bg-[#C97A2B]" />
            <span className="text-[#C97A2B] text-[10px] tracking-[0.3em] uppercase">Need Help?</span>
            <div className="w-8 h-[1px] bg-[#C97A2B]" />
          </div>
          <a
            href="tel:+358403779200"
            className="text-[#2F2F2F] text-sm hover:text-[#C97A2B] transition-colors"
            data-testid="help-phone-link"
          >
            Call us at <span className="font-medium">+358 40 377 9200</span>
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderLookup;
